<template>
    <v-row
        no-gutters
        class="px-9 pt-4"
    >
        <v-col cols="12">
            <v-row
                no-gutters
                class="my-4"
            >
                <v-col cols="12" class="d-flex">
                    <div class="title">
                        {{ $t('Member rights') }}
                    </div>
                </v-col>
            </v-row>
            <v-row
                no-gutters
                class="mb-2"
            >
                <v-col cols="12" class="d-flex">
                    <switch-button
                        :value="isUserAbleToManageContracts===1"
                        :label="$t('Allow managers to view and create their client contracts and invoices')"
                        @change="isUserAbleToManageContracts = $event ? 1 : 0"
                    />
                </v-col>
            </v-row>
            <v-row
                no-gutters
                class="mb-2"
            >
                <v-col cols="12" class="d-flex">
                    <switch-button
                        :value="isInvoiceManagementEnabledForUsers===1"
                        :label="$t('Allow members to view and create their client invoices')"
                        @change="isInvoiceManagementEnabledForUsers = $event ? 1 : 0"
                    />
                </v-col>
            </v-row>
            <v-row
                no-gutters
                class="mb-2"
            >
                <v-col cols="12" class="d-flex">
                    <switch-button
                        :value="isClientInvoicesVisibleToUsers===1"
                        :label="$t('Allow members to view their client invoices')"
                        @change="isClientInvoicesVisibleToUsers = $event ? 1 : 0"
                    />
                </v-col>
            </v-row>
            <v-row
                no-gutters
                class="mb-2 hidden"
            >
                <v-col cols="12" class="d-flex">
                    <switch-button
                        :value="allowOnlyOwnersToAddAndUpdateProducts===1"
                        :label="$t('Allow only owners to add and update products')"
                        @change="allowOnlyOwnersToAddAndUpdateProducts = $event ? 1 : 0"
                    />
                </v-col>
            </v-row>
            <v-row
                no-gutters
                class="mb-2 hidden"
            >
                <v-col cols="12" class="d-flex">
                    <switch-button
                        :value="allowMembersToSendInvoicesToClients===1"
                        :label="$t('Allow members to send invoices to clients')"
                        @change="allowMembersToSendInvoicesToClients = $event ? 1 : 0"
                    />
                </v-col>
            </v-row>
            <!-- ATM HIDDEN - NOT USED FEATURE (maybe c u in the future)
            <v-row
                no-gutters
                class="mb-2 hidden"
            >
                <v-col cols="12">
                    <switch-button
                        :value="canCompleteInvoiceRows===1"
                        :label="$t('Allow members to confirm their client invoice rows')"
                        @change="canCompleteInvoiceRows = $event ? 1 : 0"
                    />
                </v-col>
            </v-row>
            -->

            <v-row no-gutters class="mb-2 pt-6">
                <v-col cols="12" class="d-flex">
                    <div class="title">
                        {{ $t('Send PDF invoice') }}
                    </div>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12">
                    <BillingDialogEmailSettingsInputs
                        :email-service-enabled.sync="emailServiceSettings.email_service_enabled"
                        :vendor="emailServiceSettings.vendor"
                        :host.sync="emailServiceSettings.host"
                        :port.sync="emailServiceSettings.port"
                        :security.sync="emailServiceSettings.security"
                        :username.sync="emailServiceSettings.username"
                        :password.sync="emailServiceSettings.password"
                        :passwordError.sync="emailServiceSettings.password_error"
                        :from-name.sync="emailServiceSettings.from_name"
                        :vendors="emailVendors"
                        @update:vendor="onSelectVendor"
                        @send_test_email="onSendTestEmail()"
                    />
                </v-col>
            </v-row>
            <v-row dense class="mb-2">
                <v-col cols="6">
                    <v-text-field :value="invoiceCompanyRecipients"
                                  :label="$t('Invoice email')"
                                  outlined
                                  hide-details
                                  @change="invoiceCompanyRecipients = $event || ''"
                    ></v-text-field>
                </v-col>
            </v-row>

            <v-row no-gutters class="mb-2 pt-4">
                <v-col cols="12" class="d-flex">
                    <div class="title">
                        {{ $t('Default rounding') }}
                    </div>
                </v-col>
            </v-row>
            <v-row dense class="mb-2">
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="roundingOptions(false).filter(x => x.value === invoiceTimeRounding)"
                        :items="roundingOptions(false)"
                        name-key="text"
                        value-key="value"
                        :tooltip="$t('Round all times up to x')"
                        show-selected-first
                        :label="$t('Time rounding increment')"
                        :searchable="false"
                        @input="updateTimeRounding"
                    />
                </v-col>
                <v-col cols="4" class="d-flex"> <!--v-if="invoiceTimeRounding !== 0"-->
                    <dropdown
                        :selected="invoiceRoundByOptions(false).filter(x => x.value === invoiceRoundBy)"
                        :items="invoiceRoundByOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :label="$t('Apply time rounding to')"
                        :searchable="false"
                        @input="invoiceRoundBy = $event.value"
                    />
                </v-col>
            </v-row>

            <v-row no-gutters class="mb-2 pt-4">
                <v-col cols="12" class="d-flex">
                    <div class="title">
                        {{ $t('Default configuration') }}
                    </div>
                </v-col>
            </v-row>

            <v-row dense class="mb-2">
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="invoiceDatePeriodOptions(false).filter(x => x.value === invoiceDatePeriod)"
                        :items="invoiceDatePeriodOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :label="$t('Invoice date')"
                        :searchable="false"
                        @input="invoiceDatePeriod = $event.value"
                    />
                </v-col>
                <v-col cols="4" class="d-flex">
                    <text-field
                        :value="invoiceDueDatePeriod"
                        :placeholder="$t('days')"
                        :label="$t('Due date in')"
                        :suffix="$t('days')"
                        @keypress="filterNumber"
                        @change="onChangeInvoiceDueDate"
                    />
                </v-col>
                <!-- FUTURE ENABLE THIS WITH ACTUAL INTEGRATION
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="invoiceDatePeriodOptions(false).filter(x => x.value === invoiceEntryPeriod)"
                        :items="invoiceDatePeriodOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :label="$t('Invoice entry date')"
                        :searchable="false"
                        @input="invoiceEntryPeriod = $event.value"
                    />
                </v-col>
                --->
            </v-row>
            <v-row dense class="mb-2">
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="billByTimeOptions(false).filter(x => x.value === invoiceBillByTime)"
                        :items="billByTimeOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :label="$t('Bill tracked time by')"
                        :searchable="false"
                        @input="invoiceBillByTime = $event.value"
                    />
                </v-col>
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="billByFieldOptions(false).filter(x => x.value === invoiceBillByField)"
                        :items="billByFieldOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :label="$t('Bill task field by')"
                        :searchable="false"
                        @input="invoiceBillByField = $event.value"
                    />
                </v-col>
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="mergePeriodInvoicesOptions(false).filter(x => x.value === mergePeriodInvoices)"
                        :items="mergePeriodInvoicesOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :searchable="false"
                        @input="mergePeriodInvoices = $event.value"
                        :tooltip="$t('Create one invoice merging invoices from the same period')"
                        :label="$t('Invoices from the same period')"
                    />
                </v-col>
            </v-row>
            <v-row dense class="mb-2">
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="yesNoOptions(false).filter(x => x.value === hideZeroSumInvoiceRows)"
                        :items="yesNoOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :label="$t('Hide zero sum lines')"
                        :searchable="false"
                        @input="hideZeroSumInvoiceRows = $event.value"
                    />
                </v-col>
                <v-col cols="4" class="d-flex">
                    <dropdown
                        :selected="yesNoOptions(false).filter(x => x.value === timeBillable)"
                        :items="yesNoOptions(false)"
                        name-key="text"
                        value-key="value"
                        show-selected-first
                        :label="$t('By default, time entry is billable')"
                        :searchable="false"
                        @input="timeBillable = $event.value"
                    />
                </v-col>
            </v-row>
            <v-row dense class="mb-2">
                <v-col cols="4">
                    <v-text-field
                        :value="invoiceNumberPrefix"
                        :label="$t('Invoice number') + ' ' + $t('Prefix').toLowerCase()"
                        outlined
                        hide-details
                        @change="invoiceNumberPrefix = $event || ''"
                    ></v-text-field>
                </v-col>
                <v-col cols="4">
                    <v-text-field
                        :value="invoiceNumberSuffix"
                        :label="$t('Invoice number') + ' ' + $t('Suffix').toLowerCase()"
                        outlined
                        hide-details
                        @change="invoiceNumberSuffix = $event || ''"
                    ></v-text-field>
                </v-col>
            </v-row>

        </v-col>
    </v-row>
</template>

<script>

import {computed, ref, onMounted, watch} from '@vue/composition-api'
import {$t} from "@/i18n";
import {get} from '@/helpers/get-set'
import {deepClone, filterNumber} from "@/helpers/helpers";
import DialogModule from "@/store/modules/dialog";
import FeatureModule from "@/store/modules/feature";
import AppModule from "@/store/modules/app";
import BillingDialogContentHeader from '@/components/AppStore/BillingDialogContentHeader'
import BillingDialogEmailSettingsInputs from "@/components/AppStore/BillingDialogEmailSettingsInputs";
import PrimaryButton from "@/components/Buttons/PrimaryButton";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import {TAB_NAMES} from '@/views/AppStore/app-tabs.js'
import errorDialog from "@/views/ErrorDialog/errorDialog"
import AccountModule from "../../../store/modules/account";
import {
    roundingOptions,
    billByTimeOptions,
    billByFieldOptions,
    invoiceDatePeriodOptions,
    mergePeriodInvoicesOptions,
    invoiceRoundByOptions,
    yesNoOptions,
} from "@/helpers/constants"
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import {cleanNumber, formatNumber} from "@/plugins/formatting";

export default {
    name: "BillingDialogSettingsTab",
    components: {
        BillingDialogContentHeader,
        PrimaryButton,
        BillingDialogEmailSettingsInputs
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const {fetchAccount} = AccountModule();
        const {refreshAccountsInfo} = AppModule();
        const {showFeedbackNotification} = FeedbackNotificationModule();

        const {appStoreDialogGetters, appStoreDialogActions} = appStoreDialog()
        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const accountFeature = appStoreDialogGetters.getCurrentFeature() || {}
        const accountFeatureSettings = appStoreDialogGetters.getCurrentFeatureSettings() || {}

        const contractSettings = computed(() => ({
            invoice_bill_by_time: invoiceBillByTime.value,
            invoice_bill_by_field: invoiceBillByField.value,

            invoice_round_by: invoiceRoundBy.value,
            invoice_time_rounding: invoiceTimeRounding.value,
            invoice_due_date_period: invoiceDueDatePeriod.value,

            invoice_company_recipients: invoiceCompanyRecipients.value,
            invoice_number_prefix: invoiceNumberPrefix.value,
            invoice_number_suffix: invoiceNumberSuffix.value,

            invoice_date_period: invoiceDatePeriod.value,
            invoice_entry_period: invoiceEntryPeriod.value,
            merge_period_invoices: mergePeriodInvoices.value,
            time_billable: timeBillable.value,
            hide_zero_sum_invoice_rows: hideZeroSumInvoiceRows.value,
            is_user_able_to_manage_contracts: isUserAbleToManageContracts.value,
            is_invoice_management_enabled_for_users: isInvoiceManagementEnabledForUsers.value,
            is_client_invoices_visible_to_users: isClientInvoicesVisibleToUsers.value,
            allow_only_owners_to_add_and_update_products: allowOnlyOwnersToAddAndUpdateProducts.value,
            allow_members_to_send_invoices_to_clients: allowMembersToSendInvoicesToClients.value,
            can_complete_invoice_rows: canCompleteInvoiceRows.value,
        }))

        // CONTRACT SETTINGS

        // invoice
        const invoiceBillByTime = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_bill_by_time'))
        const invoiceBillByField = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_bill_by_field'))

        const invoiceTimeRounding = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_time_rounding') || 0)
        const invoiceRoundBy = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_round_by'))

        const invoiceDatePeriod = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_date_period') || 0)
        const invoiceDueDatePeriod = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_due_date_period') || 7)
        const invoiceEntryPeriod = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_entry_period') || 0)

        const invoiceCompanyRecipients = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_company_recipients') || '')
        const invoiceNumberPrefix = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_number_prefix') || '')
        const invoiceNumberSuffix = ref(get(accountFeatureSettings, 'data.contract_settings.invoice_number_suffix') || '')

        const hideZeroSumInvoiceRows = ref(get(accountFeatureSettings, 'data.contract_settings.hide_zero_sum_invoice_rows') || 0)
        const mergePeriodInvoices = ref(get(accountFeatureSettings, 'data.contract_settings.merge_period_invoices') || 0)
        const timeBillable = ref(get(accountFeatureSettings, 'data.contract_settings.time_billable') || 0)

        // rights
        const isClientInvoicesVisibleToUsers = ref(get(accountFeatureSettings, 'data.contract_settings.is_client_invoices_visible_to_users') || 0)
        const canCompleteInvoiceRows = ref(get(accountFeatureSettings, 'data.contract_settings.can_complete_invoice_rows') || 0)
        const isUserAbleToManageContracts = ref(get(accountFeatureSettings, 'data.contract_settings.is_user_able_to_manage_contracts') || 0)
        const isInvoiceManagementEnabledForUsers = ref(get(accountFeatureSettings, 'data.contract_settings.is_invoice_management_enabled_for_users') || 0)
        const allowOnlyOwnersToAddAndUpdateProducts = ref(get(accountFeatureSettings, 'data.contract_settings.allow_only_owners_to_add_and_update_products') || 0)
        const allowMembersToSendInvoicesToClients = ref(get(accountFeatureSettings, 'data.contract_settings.allow_members_to_send_invoices_to_clients') || 0)

        // email
        const emailServiceSettings = ref({
            email_service_enabled: get(accountFeatureSettings, 'data.email_settings.email_service_enabled', 0),
            vendor: get(accountFeatureSettings, 'data.email_settings.vendor'),
            host: get(accountFeatureSettings, 'data.email_settings.host'),
            port: get(accountFeatureSettings, 'data.email_settings.port'),
            security: get(accountFeatureSettings, 'data.email_settings.security'),
            username: get(accountFeatureSettings, 'data.email_settings.username'),
            password: get(accountFeatureSettings, 'data.email_settings.password'),
            password_error: null,
            from_name: get(accountFeatureSettings, 'data.email_settings.from_name'),
        })

        const onSelectVendor = function (vendor) {
            emailServiceSettings.value.vendor = vendor.value;
            emailServiceSettings.value.host = vendor.host;
            emailServiceSettings.value.port = vendor.port || 0;
            emailServiceSettings.value.security = vendor.security;
        }

        const loading = ref(false)
        const {setConfirmOptions, openConfirmDialog} = DialogModule();
        const {handleErrorResponse} = AppModule();
        const {execAppAction} = FeatureModule();
        const emailVendors = computed(() => [
            {value: "google", text: $t("Google Gmail"), host: "smtp.gmail.com", port: 587, security: "tls"},
            {value: "outlook", text: $t("Microsoft Outlook"), host: "smtp-mail.outlook.com", port: 587, security: "tls"},
            {value: "other", text: $t("Other"), host: "", port: "", security: ""},
        ])

        const sendTestEmail = function () {
            loading.value = true;
            execAppAction({
                account_id: selectedAccount.value.id,
                feature_id: accountFeature.feature_id,
                action: 'send_test_email',
                data: deepClone(emailServiceSettings.value)
            })
                .then(function (response) {
                    if (response.send_test_email) {
                        showFeedbackNotification(response.title)
                    } else {
                        errorDialog.showError(response.title)
                    }
                    loading.value = false;
                })
                .catch(ex => {
                    console.error("Email SettingsControl :: execute_action", ex);
                    loading.value = false;
                    handleErrorResponse(emailServiceSettings.value, ex);
                });
        };
        const onSendTestEmail = () => {
            setConfirmOptions({
                title: $t("Send test email"),
                confirm_text: $t("Send"),
                cancel_text: $t("Cancel"),
                action: () => sendTestEmail("send_test_email"),
            });
            openConfirmDialog();
        };

        const getBillingSettingsPayload = function () {
            return {
                contract_settings: {...contractSettings.value},
                email_settings: {...emailServiceSettings.value},
            }
        }

        const onSave = () => {
            loading.value = true;

            execAppAction({
                account_id: selectedAccount.value.id,
                feature_id: accountFeature.feature_id,
                action: "save_settings",
                data: getBillingSettingsPayload()
            })
                .then(resp => {
                    refreshAccountsInfo()
                    ctx.emit("update", resp);
                    ctx.emit("close");
                    appStoreDialogActions.updateCurrentTab(TAB_NAMES.OVERVIEW)
                    showFeedbackNotification($t('Billing settings saved'))
                })
                .catch(ex => {
                    handleErrorResponse(getBillingSettingsPayload(), ex);
                }).finally(() => {
                loading.value = false;
            });
        };
        const {fetchClassifiers, countries} = AppModule();
        fetchClassifiers({
            types: ["countries"],
            account_id: selectedAccount.value.id
        });

        const updateTimeRounding = (e) => {
            invoiceTimeRounding.value = e.value
        }

        const onChangeInvoiceDueDate = (value) => {
            let cleanedValue = Number(cleanNumber(value))
            if (!cleanedValue) {
                cleanedValue = 0
            } else if (cleanedValue > 99) {
                cleanedValue = 99
            } else if (cleanedValue < 0) {
                cleanedValue = 0
            }
            invoiceDueDatePeriod.value = cleanedValue
        }

        watch(() => props.isSelected, () => {
            if (props.isSelected) {
                appStoreDialogActions.setFooterActions([
                    {
                        text: $t("Save"),
                        class: "primary",
                        loading: loading,
                        click: onSave
                    }
                ]);
            }
        })

        return {
            countries,
            loading,
            emailVendors,
            emailServiceSettings,
            accountFeature,
            accountFeatureSettings,
            onSelectVendor,
            onSave,
            onSendTestEmail,
            isClientInvoicesVisibleToUsers,
            canCompleteInvoiceRows,
            isUserAbleToManageContracts,
            isInvoiceManagementEnabledForUsers,
            roundingOptions,
            invoiceTimeRounding,
            updateTimeRounding,
            allowOnlyOwnersToAddAndUpdateProducts,
            allowMembersToSendInvoicesToClients,
            invoiceBillByTime,
            billByTimeOptions,

            invoiceBillByField,
            billByFieldOptions,

            invoiceRoundBy,
            invoiceDueDatePeriod,

            invoiceCompanyRecipients,
            invoiceNumberPrefix,
            invoiceNumberSuffix,

            formatNumber,
            filterNumber,
            cleanNumber,
            invoiceDatePeriodOptions,
            invoiceDatePeriod,
            invoiceEntryPeriod,
            mergePeriodInvoices,
            yesNoOptions,
            timeBillable,
            hideZeroSumInvoiceRows,
            invoiceRoundByOptions,
            mergePeriodInvoicesOptions,
            onChangeInvoiceDueDate,
        };
    }
};
</script>
